/**
 * List of allowed popular email provider domains.
 * Temporary/disposable email services are not included.
 */
const ALLOWED_EMAIL_DOMAINS = new Set([
  // Google
  'gmail.com',
  // Yahoo
  'yahoo.com',
  'yahoo.fr',
  'yahoo.co.uk',
  'yahoo.co.jp',
  'yahoo.de',
  'yahoo.es',
  'yahoo.it',
  'yahoo.com.br',
  'yahoo.com.au',
  'yahoo.ca',
  'yahoo.com.ar',
  'yahoo.in',
  'yahoo.com.mx',
  'ymail.com',
  // Microsoft (Outlook & Hotmail)
  'outlook.com',
  'hotmail.com',
  'hotmail.fr',
  'hotmail.co.uk',
  'hotmail.de',
  'hotmail.es',
  'hotmail.it',
  // Apple
  'icloud.com',
  'me.com',
  'mac.com',
]);

/**
 * Returns the domain part of an email address (lowercased).
 */
function getEmailDomain(email: string): string {
  const atIndex = email.lastIndexOf('@');
  if (atIndex === -1) return '';
  return email.slice(atIndex + 1).toLowerCase();
}

/**
 * Checks whether the email's domain belongs to a known popular provider.
 * Returns false for temporary/disposable email services.
 */
export function isAllowedEmailProvider(email: string): boolean {
  const domain = getEmailDomain(email);
  if (!domain) return false;
  return ALLOWED_EMAIL_DOMAINS.has(domain);
}

/**
 * Normalizes an email address by removing the plus alias part.
 * e.g. "test+account1@gmail.com" → "test@gmail.com"
 *
 * This ensures that plus-aliased variants of the same address
 * are treated as the same user.
 */
export function normalizeEmail(email: string): string {
  const atIndex = email.lastIndexOf('@');
  if (atIndex === -1) return email.toLowerCase();

  const local = email.slice(0, atIndex);
  const domain = email.slice(atIndex + 1).toLowerCase();

  const plusIndex = local.indexOf('+');
  const normalizedLocal = plusIndex !== -1 ? local.slice(0, plusIndex) : local;

  return `${normalizedLocal.toLowerCase()}@${domain}`;
}
